// Phase 44 — PRICE-02 — useMarketBand hook (Plan 44-06).
//
// Orchestrates the market-band pricing agent for BOTH UI entry points:
//   - Explicit "Suggest price" button on canvas price field (D-07)
//   - Post-Comparables CTA row in CopilotFeed (D-08)
// Both call `trigger.mutate()` — no duplicate dispatch logic.
//
// Contract refs:
//   44-CONTEXT D-07 (explicit trigger), D-09 (dismiss preserves server history
//   for scorecard), D-11 (Apply is immediate — no AiResultReview dialog),
//   D-17 (silent background sync — no toast on dispatch success/failure).
//
// Apply atomicity (D-11 / T-44-05): setValue('price', ...) + setProvenance(...)
// are called in the SAME event-handler tick. React 18 auto-batches state
// updates within one handler, so the two react-hook-form writes land in a
// single commit. Both writes go through react-hook-form — no side channel,
// no window.* mutation (threat model T-44-05 mitigation).
//
// Pattern analog: useAIComparables.ts (dispatch → poll → terminal).

import { useState, useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useFormContext } from 'react-hook-form';
import { useCan } from '@/modules/tenant/hooks/useTenantAuth';
import { aiApi } from '../services/aiApi';
import { guardPaidMutation } from './paidAiGuard';
import { useAIJobStatus } from './useAIJobStatus';
import { useProvenance } from '@/modules/inventory/components/canvas/hooks/useProvenance';
import type { BoatFormData } from '../types/boat.types';
import type { MarketBandOutput } from '@marine/contracts/types/ai.types';

export interface UseMarketBandReturn {
  /** TanStack mutation — `mutate()` / `mutateAsync()` kicks off dispatch. */
  trigger: ReturnType<typeof useMutation<
    { job_id: string; status: 'pending' | 'accepted' },
    Error,
    void
  >>;
  /** useAIJobStatus(jobId) passthrough — consumers read .data?.status / .data?.result. */
  jobStatus: ReturnType<typeof useAIJobStatus>;
  /** Live jobId once the 202 ACK lands; undefined before dispatch. */
  jobId: string | undefined;
  /** Derived: MarketBandOutput once jobStatus flips to 'completed', else null. */
  result: MarketBandOutput | null;
  /** Apply the suggestion to the price field + set AI provenance in one tick. */
  apply: () => void;
  /** Session-only hide — does NOT clear ListingSession.agent_history (D-09). */
  dismiss: () => void;
  /** Session-only flag; resets on fresh dispatch. */
  dismissed: boolean;
  /** True while a job is in flight (button / chip show loading state). */
  isActive: boolean;
}

export function useMarketBand(boatId: string | null): UseMarketBandReturn {
  const [jobId, setJobId] = useState<string | undefined>();
  const [dismissed, setDismissed] = useState(false);

  // Form integration — `apply()` writes both value + provenance via RHF
  // per Pitfall #5 in useProvenance (dotted-path writes only, never bulk
  // replace `_provenance`).
  //
  // Field target: `our_price` — this is the dealer-facing "Our price" field
  // rendered in the canvas PricingSection (Plan 07). BoatFormData also
  // exposes a generic `price` field but the canvas never renders it; the
  // dealer-facing column is `our_price`. The useProvenance chip therefore
  // also hangs off `our_price` so the post-apply ProvenanceChip renders on
  // the same row the chip was pre-apply on.
  const { setValue } = useFormContext<BoatFormData>();
  const { set: setProvenance } = useProvenance('our_price');

  // ACL-09 (#611, D-22) — market band is paid AI: without inventory.ai.generate
  // the trigger is inert and no request is sent.
  const canGenerate = useCan('inventory.ai.generate');

  const dispatch = useMutation({
    mutationFn: async () => {
      // Guard per D-07 + Pitfall #8: market-band requires a persisted
      // inventory_item_id (it reads stored comparables). Hook callers
      // should disable the button when boatId is null; this is a
      // defense-in-depth throw.
      if (!boatId) {
        throw new Error('boatId required — save draft before requesting price suggestion');
      }
      return aiApi.dispatchMarketBand(boatId);
    },
    onSuccess: (data) => {
      setJobId(data.job_id);
      setDismissed(false);  // fresh dispatch → un-dismiss any prior session hide
    },
  });
  const trigger = guardPaidMutation(dispatch, canGenerate);

  const jobStatus = useAIJobStatus(jobId);

  // D-01 output contract; cast is safe because the Laravel callback writes
  // the Zod-validated MarketBandOutput into ai_job_statuses.result and
  // useAIJobStatus returns that shape verbatim.
  const result: MarketBandOutput | null =
    jobStatus.data?.status === 'completed' && jobStatus.data.result
      ? (jobStatus.data.result as unknown as MarketBandOutput)
      : null;

  /**
   * Apply the suggestion. Atomic per D-11 (React 18 auto-batches within an
   * event handler). Guards on the D-04 zero-comps short-circuit — if the
   * agent returned `suggestedPrice: null`, there is nothing to apply.
   */
  const apply = useCallback(() => {
    if (!result) return;
    if (result.suggestedPrice === null) return;

    // Both writes land in the same tick → React 18 batches into one commit.
    // Target field is `our_price` (the dealer-facing price field rendered in
    // the canvas PricingSection) — matches the useProvenance('our_price')
    // subscription above so the post-apply ProvenanceChip mounts on the same
    // row.
    setValue('our_price', result.suggestedPrice, { shouldDirty: true });
    setProvenance({
      source: 'AI',
      value: result.suggestedPrice,
      confidence: result.confidence,
      reasoning: result.reasoning,
      updatedAt: new Date().toISOString(),
    });
  }, [result, setValue, setProvenance]);

  /**
   * Dismiss — session-only hide. Intentionally does NOT:
   *   - clear the form value or provenance
   *   - tell the server to forget the market-band run
   * ListingSession.agent_history retains the run so scorecard (D-12) can
   * still read the suggestion as a reference for its pricing dimension.
   */
  const dismiss = useCallback(() => {
    setDismissed(true);
  }, []);

  const isActive =
    !!jobId &&
    jobStatus.data?.status !== 'completed' &&
    jobStatus.data?.status !== 'failed';

  return {
    trigger,
    jobStatus,
    jobId,
    result,
    apply,
    dismiss,
    dismissed,
    isActive,
  };
}
